import { useEffect, useMemo } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import PropTypes from "prop-types";
import {
  Button,
  ButtonGroup,
  Heading,
  LabeledValue,
  Picker,
  PickerItem,
  Text,
  TextField,
} from "@react-spectrum/s2";
import NoElementsIcon from "@react-spectrum/s2/illustrations/linear/NoElements";

import { HeaderTitle } from "@app/components/Header";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import { S3Asset } from "@app/components/S3Asset";
import { AudioPlayer } from "@app/components/AudioPlayer";
import { useVideo, useVideoTemplate } from "@app/hooks/videos";
import { usePresignURL } from "@app/hooks/s3";
import {
  isDemoProject,
  useVideoWizardDemo,
  useWizardDemoEnabled,
} from "@app/hooks/useVideoWizardDemo";
import { resolveReferenceAssets } from "@app/hooks/imageVariations/resolve";
import { CampaignVisualsPreview } from "./CampaignVisualsPreview";
import { CampaignField } from "./CampaignPreview";
import { ImageVariationCard } from "./ImageVariations";
import { TemplateColorAsset, TemplateImageAsset } from "./TemplateAsset";
import {
  getContinueLabel,
  getVariationReferenceGroupId,
  isCampaignVisualsStep,
  isImagesStep,
  WIZARD_STEP,
} from "./wizardSteps";
import { Step } from "./new/Step";
import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const contentStyle = style({
  display: "flex",
  flexDirection: "column",
  gap: 32,
  width: "full",
  maxWidth: 1200,
  marginX: "auto",
  padding: 24,
});
const assetGridStyle = style({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "start",
  justifyContent: "center",
  gap: 32,
});

export const WizardDemoBoundary = ({ children, isWizard = false }) => {
  const { id } = useParams();
  const location = useLocation();
  const enabled = useWizardDemoEnabled();
  const video = useVideo(id, { enabled: !!id && enabled });
  if (!id || !enabled) {
    return children;
  }
  if (video.isPending) {
    return <Step.Loading title="Loading your project" />;
  }
  if (video.isError) {
    return <Step.Error title="Failed to load project" onRetry={video.refetch} />;
  }
  if (!isDemoProject(enabled, video.data)) {
    return children;
  }
  if (!isWizard) {
    return <Navigate to={`/projects/new/${id}`} replace />;
  }
  const sessionKey = location.state?.wizardDemoSession ?? location.key;
  return (
    <DemoVideoWizard
      key={`${id}:${sessionKey}`}
      project={video.data}
      sessionKey={sessionKey}
    />
  );
};

const DemoPicker = ({ demo, field, label, items = [] }) => {
  const savedValue = demo.values[field];
  const options = items.map((item) => ({
    id: String(item.id ?? item.key),
    label: item.label ?? item.name ?? item.id ?? item.key,
  }));
  if (
    savedValue != null &&
    !options.some((item) => item.id === String(savedValue))
  ) {
    options.push({ id: String(savedValue), label: String(savedValue) });
  }
  const available = savedValue != null && savedValue !== "";
  return (
    <Picker
      label={label}
      value={demo.value(field) == null ? null : String(demo.value(field))}
      onChange={(key) => key != null && demo.chooseValue(field)}
      isDisabled={!available}
      description={
        available
          ? "Select an option to reveal the project's saved value."
          : "No saved value is available in this project."
      }
    >
      {options.map((item) => (
        <PickerItem key={item.id} id={item.id} textValue={String(item.label)}>
          {String(item.label)}
        </PickerItem>
      ))}
    </Picker>
  );
};

const DemoTextField = ({ demo, field, label, isRequired = false }) => (
  <TextField
    label={label}
    isRequired={isRequired}
    value={demo.value(field) ?? ""}
    onChange={(value) => value && demo.chooseValue(field)}
    isDisabled={!demo.values[field]}
    description={
      demo.values[field]
        ? "Enter a value to reveal the project's saved value."
        : "No saved value is available in this project."
    }
  />
);

const DemoAudio = ({ asset }) => {
  const url = usePresignURL(asset.filePath);
  return (
    <div>
      <Heading>{asset.label ?? asset.name}</Heading>
      <AudioPlayer
        url={asset.src ?? url.data?.presignURL}
        isAudioJobScheduled={url.isLoading}
        isEditorEmpty={!asset.src && !url.data?.presignURL}
      />
    </div>
  );
};

const DemoVariation = ({ demo, variation, label, description }) => {
  const available = demo.hasSavedVariation(variation);
  const progress = demo.progressByKey[variation.key];
  return (
    <div className={style({ width: 280, maxWidth: "full" })}>
      {!!label && <LabeledValue label={label} value="" />}
      <ImageVariationCard
        variation={variation}
        asset={demo.project.assets.find((asset) => asset.name === variation.key)}
        assets={demo.project.assets}
        progress={progress}
        isLoading={!!progress}
        isIdle={!demo.revealedAssets.has(variation.key) && !progress}
      />
      {!!description && <Text>{description}</Text>}
      {!available && <Text>No saved image is available for this variation.</Text>}
      <Button
        variant="secondary"
        isDisabled={!available || !!progress}
        onPress={() => demo.simulateVariations([variation.key])}
      >
        {demo.revealedAssets.has(variation.key) ? "Regenerate" : "Generate"}
      </Button>
    </div>
  );
};

const DemoTemplateAsset = ({ demo, template, asset, ...caption }) => {
  const saved = demo.savedAsset(asset.name);
  const selected = demo.project.assets.find((item) => item.name === asset.name);
  const available = !!(saved?.filePath || saved?.src || saved?.color);
  const scope = {
    video: { data: demo.project },
    template: { data: template },
    selectedCampaign: demo.saved.campaigns?.find(
      (campaign) => campaign.id === demo.saved.selectedCampaignIds?.[0],
    ),
  };
  return (
    <div className={style({ width: 280 })}>
      {asset.type === "color" && !available ? (
        <LabeledValue label={asset.label ?? asset.name} value="" />
      ) : asset.type === "color" ? (
        <TemplateColorAsset
          asset={asset}
          selectedAsset={selected ?? { name: asset.name }}
          scope={scope}
          {...caption}
          colors={saved?.color ? [saved.color] : []}
          onChange={() => demo.chooseAsset(asset.name)}
        />
      ) : (
        <TemplateImageAsset
          asset={asset}
          selectedAsset={selected}
          scope={scope}
          {...caption}
          isDisabled={!available || demo.uploadingKeys.has(asset.name)}
          onDrop={(_items, name) => demo.chooseAsset(name)}
        />
      )}
      {!available && <Text>No saved asset is available in this project.</Text>}
    </div>
  );
};

const DemoBranding = ({ demo, template }) => (
  <div className={assetGridStyle}>
    {(template.assets ?? [])
      .filter((asset) => ["image", "color"].includes(asset.type))
      .map((asset) => (
        <DemoTemplateAsset key={asset.name} demo={demo} template={template} asset={asset} />
      ))}
  </div>
);

const DemoCampaign = ({ demo, template, campaign, persona }) => {
  const groups = useMemo(() => {
    const result = new Map();
    for (const variation of demo.variations) {
      const id = getVariationReferenceGroupId(variation);
      if (!result.has(id)) {
        result.set(id, {
          id,
          variations: [],
          referenceAssets: resolveReferenceAssets(demo.project.assets, variation),
        });
      }
      result.get(id).variations.push(variation);
    }
    return [...result.values()];
  }, [demo.variations, demo.project.assets]);
  if (!campaign) {
    return <Text>No saved campaign is available in this project.</Text>;
  }
  if (demo.step.campaignStructure?.length) {
    return (
      <CampaignVisualsPreview
        campaignStructure={demo.step.campaignStructure}
        selectedCampaign={campaign}
        selectedPersona={persona}
        canEditCampaign={false}
        video={{ data: demo.project }}
        templateData={template}
        imageVariations={{
          groups,
          generate: (keys) => demo.simulateVariations(keys),
        }}
        isGenerateDisabled={
          !!Object.keys(demo.progressByKey).length ||
          demo.variations.some((variation) => !demo.hasSavedVariation(variation))
        }
        onUploadAsset={(_items, name) => demo.chooseAsset(name)}
        onColorChange={(_color, name) => demo.chooseAsset(name)}
        renderVariation={(variation, caption) => (
          <DemoVariation key={variation.key} demo={demo} variation={variation} {...caption} />
        )}
        renderAsset={(asset, caption) => (
          <DemoTemplateAsset demo={demo} template={template} asset={asset} {...caption} />
        )}
      />
    );
  }
  return (
    <div className={contentStyle}>
      {Object.entries(campaign)
        .filter(
          ([key, value]) =>
            !["id", "createdAt", "updatedAt"].includes(key) &&
            typeof value === "string" &&
            !!value,
        )
        .map(([key]) => (
          <CampaignField
            key={key}
            label={key}
            value={key}
            selectedCampaign={campaign}
            selectedPersona={persona}
            isDisabled
          />
        ))}
    </div>
  );
};

const DemoStepLoading = ({ demo }) => {
  const { step, enterAction } = demo;
  const isCampaign =
    isCampaignVisualsStep(step) || step.id === WIZARD_STEP.CAMPAIGN;
  return (
    <Step.Loading
      title={
        enterAction?.pending?.title ??
        (isCampaign
          ? `Preparing your ${step.contentLabel ?? "campaign"}`
          : step.id === WIZARD_STEP.BRANDING
            ? "Setting up your project"
            : step.name)
      }
      subtitle={
        enterAction?.pending?.subtitle ??
        "This may take a few moments, so please keep this tab open."
      }
      ariaLabel={isCampaign ? "Generating campaign" : "Loading"}
      icon={isCampaign ? <NoElementsIcon /> : undefined}
    />
  );
};

const DemoStepContent = ({ demo, template, campaign, persona }) => {
  const { step, saved } = demo;
  if (step.id === WIZARD_STEP.SELECT_TEMPLATE) {
    return (
      <DemoPicker
        demo={demo}
        field="templateId"
        label="Template"
        items={[{ id: saved.templateId, label: template.name }]}
      />
    );
  }
  if (step.id === WIZARD_STEP.ACCOUNT_DETAILS) {
    return (
      <>
        <DemoPicker
          demo={demo}
          field="sfType"
          label="Account details"
          items={[
            { id: "accountName", label: "DR Number / Account" },
            { id: "drNumber", label: "DR Number" },
            { id: "none", label: "None" },
          ]}
        />
        {demo.value("sfType") === "accountName" && (
          <DemoTextField demo={demo} field="accountName" label="Account name" isRequired />
        )}
        {demo.value("sfType") === "drNumber" && (
          <DemoPicker demo={demo} field="drNumber" label="DR Number" />
        )}
      </>
    );
  }
  if (step.id === WIZARD_STEP.CUSTOMER_DETAILS) {
    return (
      <>
        <DemoTextField demo={demo} field="customerName" label="Brand name" isRequired />
        {!!saved.product && <DemoTextField demo={demo} field="product" label="Product" />}
        {!!saved.hint && <DemoTextField demo={demo} field="hint" label="Additional information" />}
        {!!template.personas?.length && (
          <DemoPicker demo={demo} field="personaId" label="Persona" items={template.personas} />
        )}
        {!!template.verticals?.length && (
          <DemoPicker demo={demo} field="verticalKey" label="Industry" items={template.verticals} />
        )}
        {demo.values.include3DDigitalTwin != null && (
          <DemoPicker
            demo={demo}
            field="include3DDigitalTwin"
            label="Include 3D digital twin"
            items={[{ id: "true", label: "Yes" }, { id: "false", label: "No" }]}
          />
        )}
      </>
    );
  }
  if (step.id === WIZARD_STEP.BRANDING) {
    return (
      <>
        <DemoBranding demo={demo} template={template} />
        {!!step.campaignStructure?.length && (
          <DemoCampaign demo={demo} template={template} campaign={campaign} persona={persona} />
        )}
      </>
    );
  }
  if (isCampaignVisualsStep(step) || step.id === WIZARD_STEP.CAMPAIGN) {
    return <DemoCampaign demo={demo} template={template} campaign={campaign} persona={persona} />;
  }
  if (isImagesStep(step)) {
    return (
      <div className={assetGridStyle}>
        {demo.variations.map((variation) => (
          <DemoVariation key={variation.key} demo={demo} variation={variation} />
        ))}
      </div>
    );
  }
  if (step.id === WIZARD_STEP.AUDIO) {
    return (
      <>
        <DemoPicker demo={demo} field="musicPath" label="Music track" items={template.musicTracks} />
        {!!demo.value("musicPath") && (
          <DemoAudio asset={{ name: "Music track", filePath: saved.musicPath }} />
        )}
      </>
    );
  }
  if (step.id === WIZARD_STEP.VOICEOVER) {
    return (
      <>
        <DemoPicker
          demo={demo}
          field="voiceoverMode"
          label="Voiceover"
          items={[
            { id: "original", label: "Original" },
            { id: "edit", label: "Customize" },
            { id: "upload", label: "Upload" },
            { id: "none", label: "None" },
          ]}
        />
        {!!demo.value("voiceoverMode") &&
          (saved.assets ?? [])
            .filter((asset) => asset.type === "audio")
            .map((asset) => <DemoAudio key={asset.name} asset={asset} />)}
      </>
    );
  }
  if (step.id === WIZARD_STEP.SCENES) {
    return campaign?.scenes?.length ? (
      campaign.scenes.map((scene, index) => (
        <div key={scene.id ?? index}>
          <Heading>{scene.name ?? `Scene ${index + 1}`}</Heading>
          {Object.entries(scene)
            .filter(([key, value]) => key !== "id" && typeof value === "string")
            .map(([key, value]) => <LabeledValue key={key} label={key} value={value} />)}
        </div>
      ))
    ) : <Text>No saved scenes are available in this project.</Text>;
  }
  if (step.id === WIZARD_STEP.GENERATE_VIDEO) {
    return saved.videoPath ? (
      <>
        <S3Asset type="video" filePath={saved.videoPath} alt={saved.name} />
        <Button variant="secondary" onPress={demo.replayStep}>
          Regenerate video
        </Button>
      </>
    ) : <Text>No saved video is available in this project.</Text>;
  }
  if (step.id === WIZARD_STEP.BANNERS) {
    const renditions = saved.templateAssetsRenditions ?? [];
    return renditions.length ? (
      <div className={assetGridStyle}>
        {renditions.map((rendition, index) => (
          <div key={rendition.documentId ?? index}>
            <LabeledValue label="Saved banner document" value={rendition.documentId ?? ""} />
            {rendition.renditionUrl ? (
              <S3Asset src={rendition.renditionUrl} alt={`Banner ${index + 1}`} />
            ) : (
              <Text>No saved banner preview is available. Live export is disabled in demo mode.</Text>
            )}
          </div>
        ))}
      </div>
    ) : <Text>No saved banners are available in this project.</Text>;
  }
  if (step.id === WIZARD_STEP.FIGMA_HANDOFF) {
    return (
      <>
        <Text>Creating or duplicating Figma files is disabled in demo mode.</Text>
        <div className={assetGridStyle}>
          {(saved.assets ?? [])
            .filter((asset) => asset.type === "image" && asset.filePath)
            .map((asset) => (
              <div key={asset.name} className={style({ width: 280 })}>
                <LabeledValue label={asset.name} value="" />
                <S3Asset filePath={asset.filePath} alt={asset.name} />
              </div>
            ))}
        </div>
      </>
    );
  }
  if (step.viewless) {
    return null;
  }
  return <Text>No saved preview is available for this step.</Text>;
};

const DemoReplay = ({ project, template, sessionKey }) => {
  const navigate = useNavigate();
  const demo = useVideoWizardDemo(project, template);
  const { step, index, steps, goTo, isStepPending } = demo;
  const campaign = demo.saved.campaigns?.find(
    (item) => item.id === demo.saved.selectedCampaignIds?.[0],
  );
  const persona = template.personas?.find((item) => item.id === demo.saved.personaId);
  useBreadcrumbs([
    { label: "Projects", href: "/projects" },
    { label: `${project.name ?? "Project"} - Demo mode` },
  ]);
  useEffect(() => {
    if (step) {
      navigate(`/projects/new/${project.id}/${step.id}`, {
        replace: true,
        state: { wizardDemoSession: sessionKey },
      });
    }
  }, [navigate, project.id, step, sessionKey]);
  useEffect(() => {
    if (step?.viewless && !isStepPending && index + 1 < steps.length) {
      goTo(index + 1);
    }
  }, [step, isStepPending, goTo, index, steps.length]);
  if (!step) {
    return <Step.Error title="This template has no demo steps" />;
  }

  const requiredAssets = (template.assets ?? []).filter(
    (asset) => ["image", "color"].includes(asset.type) && !asset.isOptional,
  );
  let canContinue = !demo.isStepPending && !Object.keys(demo.progressByKey).length;
  if (step.id === WIZARD_STEP.SELECT_TEMPLATE) {
    canContinue &&= !!demo.value("templateId");
  } else if (step.id === WIZARD_STEP.ACCOUNT_DETAILS) {
    canContinue &&= !!demo.value("sfType") &&
      (demo.value("sfType") === "none" ||
        !!demo.value(demo.value("sfType")));
  } else if (step.id === WIZARD_STEP.CUSTOMER_DETAILS) {
    canContinue &&= !!demo.value("customerName") &&
      (!demo.values.personaId || !!demo.value("personaId")) &&
      (!demo.values.verticalKey || !!demo.value("verticalKey")) &&
      (demo.values.include3DDigitalTwin == null || demo.value("include3DDigitalTwin") != null);
  } else if (step.id === WIZARD_STEP.BRANDING) {
    canContinue &&= requiredAssets.every((asset) => demo.revealedAssets.has(asset.name)) &&
      !demo.uploadingKeys.size;
  } else if (step.id === WIZARD_STEP.AUDIO) {
    canContinue &&= !demo.values.musicPath || !!demo.value("musicPath");
  } else if (step.id === WIZARD_STEP.VOICEOVER) {
    canContinue &&= !!demo.value("voiceoverMode");
  }

  return (
    <div className={contentStyle}>
      <title>{`${project.name ?? "Project"} | Demo mode | BeStory`}</title>
      <HeaderTitle><Text>{project.name ?? "Project"} - Demo mode</Text></HeaderTitle>
      <Text>Demo mode: actions reveal saved content. This project will not be changed.</Text>
      <Picker
        label="Wizard step"
        value={step.id}
        onChange={(key) => {
          const next = steps.findIndex((entry) => entry.id === key);
          if (next <= demo.furthestStep) demo.goTo(next);
        }}
      >
        {steps.filter((entry) => !entry.viewless).map((entry) => (
          <PickerItem
            key={entry.id}
            id={entry.id}
            isDisabled={steps.indexOf(entry) > demo.furthestStep}
          >
            {entry.name}
          </PickerItem>
        ))}
      </Picker>
      {demo.isStepPending ? (
        <DemoStepLoading demo={demo} />
      ) : (
        <Step styles={contentStyle}>
          <Step.Header title={step.name} subtitle={step.description} />
          <DemoStepContent demo={demo} template={template} campaign={campaign} persona={persona} />
        </Step>
      )}
      <ButtonGroup>
        <Button
          variant="secondary"
          isDisabled={index === 0}
          onPress={() => {
            let previous = index - 1;
            while (previous >= 0 && steps[previous].viewless) previous -= 1;
            demo.goTo(previous);
          }}
        >
          Back
        </Button>
        {index + 1 < steps.length ? (
          <Button isDisabled={!canContinue} onPress={() => demo.goTo(index + 1)}>
            {getContinueLabel(steps[index + 1])}
          </Button>
        ) : (
          <Button isDisabled={!canContinue} onPress={() => navigate("/projects")}>
            Back to projects
          </Button>
        )}
      </ButtonGroup>
    </div>
  );
};

// Keep mutation-owning wizard hooks, actions, dialogs and toolbars unmounted.
export const DemoVideoWizard = ({ project, sessionKey }) => {
  const template = useVideoTemplate(project.templateId);
  if (template.isPending) {
    return <Step.Loading title="Loading your template" />;
  }
  if (template.isError) {
    return <Step.Error title="Failed to load template" onRetry={template.refetch} />;
  }
  return <DemoReplay project={project} template={template.data} sessionKey={sessionKey} />;
};

DemoVideoWizard.propTypes = {
  project: PropTypes.object.isRequired,
  sessionKey: PropTypes.string.isRequired,
};

WizardDemoBoundary.propTypes = {
  children: PropTypes.node,
  isWizard: PropTypes.bool,
};
DemoPicker.propTypes = {
  demo: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  items: PropTypes.array,
};
DemoTextField.propTypes = {
  demo: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
};
DemoAudio.propTypes = { asset: PropTypes.object.isRequired };
DemoVariation.propTypes = {
  demo: PropTypes.object.isRequired,
  variation: PropTypes.object.isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
};
DemoTemplateAsset.propTypes = {
  demo: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  asset: PropTypes.object.isRequired,
};
DemoBranding.propTypes = {
  demo: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
};
DemoCampaign.propTypes = {
  demo: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  campaign: PropTypes.object,
  persona: PropTypes.object,
};
DemoStepLoading.propTypes = { demo: PropTypes.object.isRequired };
DemoStepContent.propTypes = DemoCampaign.propTypes;
DemoReplay.propTypes = {
  project: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  sessionKey: PropTypes.string.isRequired,
};
